import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const STORAGE_KEY = "ind_show_money_values";

type MoneyVisibilityContextValue = {
  showMoneyValues: boolean;
  toggleShowMoneyValues: () => void;
  setShowMoneyValues: (value: boolean) => void;
};

const MoneyVisibilityContext = createContext<MoneyVisibilityContextValue | undefined>(undefined);

export function MoneyVisibilityProvider({ children }: { children: ReactNode }) {
  const [showMoneyValues, setShowMoneyValuesState] = useState<boolean>(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (storedValue === null) {
      return;
    }

    setShowMoneyValuesState(storedValue === "true");
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, String(showMoneyValues));
  }, [showMoneyValues]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || event.newValue === null) {
        return;
      }

      setShowMoneyValuesState(event.newValue === "true");
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setShowMoneyValues = useCallback((value: boolean) => {
    setShowMoneyValuesState(value);
  }, []);

  const toggleShowMoneyValues = useCallback(() => {
    setShowMoneyValuesState((previous) => !previous);
  }, []);

  const value = useMemo(
    () => ({
      showMoneyValues,
      toggleShowMoneyValues,
      setShowMoneyValues,
    }),
    [setShowMoneyValues, showMoneyValues, toggleShowMoneyValues],
  );

  return (
    <MoneyVisibilityContext.Provider value={value}>
      {children}
    </MoneyVisibilityContext.Provider>
  );
}

export function useMoneyVisibility() {
  const context = useContext(MoneyVisibilityContext);
  if (!context) {
    throw new Error("useMoneyVisibility deve ser usado dentro de MoneyVisibilityProvider");
  }
  return context;
}
